/**
 * The station-volumes table (spec §4c): every bin, at full resolution, in the
 * order the reader asked for.
 *
 * This is the view a quantity surveyor quotes from, so it is a real `<table>`
 * with a `<caption>`, `scope="col"` headers and `aria-sort` — not a grid of
 * divs — every column of figures is `tabular-nums`, units are stated once in
 * the header rather than repeated on every cell, and a bin the worker flagged
 * as low coverage says so in text and glyph, never in colour alone.
 *
 * **The body is windowed.** The spec allows 20 000 bins; rendering them all is
 * ~180 000 DOM nodes and a dock that takes seconds to open. Rows are a fixed
 * 28 px, so the visible slice is arithmetic — `scrollTop / ROW_HEIGHT_PX` —
 * and two spacer rows keep the scrollbar honest. For the same reason the
 * hover tint is CSS (`:hover`, plus a `data-hover` attribute set imperatively
 * for the chart-driven hover): a hover must never re-render the body.
 *
 * Sorting and filtering are pure functions in
 * `@/lib/stationVolumes/tableModel`; this file wires them to headers, a
 * search box and two column toggles.
 */
import { alignmentWalkAtom } from '@/atoms/alignment.atom';
import { stationVolumeHoverBinAtom, stationVolumeSelectionAtom } from '@/atoms/stationVolumes.atom';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { SEVERITY } from '@/components/VerdictChip';
import { binCentreStation, totalsForBins } from '@/lib/stationVolumes/aggregate';
import {
  formatM2,
  formatM3Row,
  formatPct,
  formatSigned,
  formatStationRange,
  m3Formatter,
  staOnly,
} from '@/lib/stationVolumes/format';
import {
  crossfallFor,
  DEFAULT_SORT,
  endAreaFor,
  isDefaultSort,
  nextSort,
  parseStationQuery,
  revealScrollTop,
  sortBins,
  stationMatchesBin,
  type StationSort,
  type StationSortKey,
} from '@/lib/stationVolumes/tableModel';
import type { StationBin, StationVolumesAnalysisKind, StationVolumesJson } from '@/lib/stationVolumes/types';
import { mToMm } from '@/lib/surfaceExpectation';
import { useAtom, useAtomValue, useSetAtom, useStore } from 'jotai';
import { useCallback, useEffect, useId, useMemo, useRef, useState, type KeyboardEvent, type MouseEvent } from 'react';
import { useTranslation } from 'react-i18next';

export interface StationVolumeTableProps {
  json: StationVolumesJson;
  layerId: string;
  /**
   * Which analysis produced the run — copy only. The "Cells" column counts
   * cells with data on BOTH inputs, and on a deviation run those two inputs
   * are the point cloud and the design model rather than two surfaces.
   */
  kind?: StationVolumesAnalysisKind;
}

/** Every row is exactly this tall, which is what makes the window arithmetic
 *  exact — and it is over the 24 px hit-target floor (spec §5 A11y point 5). */
const ROW_HEIGHT_PX = 28;
/** Rows kept rendered either side of the viewport, so a fast scroll or an
 *  arrow-key step never shows a gap. */
const OVERSCAN_ROWS = 8;
/** Used until the ResizeObserver reports — jsdom and the first paint. */
const DEFAULT_VIEWPORT_PX = 320;

/**
 * The rules Tailwind cannot express against the `--sv-*` tokens, kept next to
 * the markup that needs them. The hover and walk tints live here rather than
 * in inline styles precisely so that hovering a row changes no React state.
 */
const TABLE_CSS = `
.sv-row:hover,.sv-row[data-hover='true']{background:color-mix(in srgb, currentColor 6%, transparent);}
.sv-row>td:first-child{border-left:2px solid transparent;}
.sv-row[aria-current]{background:color-mix(in srgb, var(--sv-walk) 22%, transparent);}
.sv-row[aria-current]>td:first-child{border-left-color:var(--sv-walk);}
@media (prefers-reduced-motion:no-preference){
  .sv-row{transition:background-color 120ms cubic-bezier(.2,0,0,1);}
}
@media (forced-colors:active){
  .sv-row[aria-current]{outline:2px solid Highlight;outline-offset:-2px;}
}
`;

interface ColumnSpec {
  key: StationSortKey;
  label: string;
  /** Stated once, here, and never on a cell. */
  unit?: string;
  help?: string;
  align: 'left' | 'right';
}

export const StationVolumeTable = ({ json, layerId, kind = 'surfaceDifference' }: StationVolumeTableProps) => {
  const { t } = useTranslation('project');
  /** The one column whose explanation names the two compared inputs. */
  const cellsHelp = t(
    kind === 'deviation' ? 'stationVolumes.table.columnCellsHelpDeviation' : 'stationVolumes.table.columnCellsHelp',
  );
  /** What a low-coverage flag was measured against: the reference surface's
   *  footprint on a current run, the whole swath on an older one. */
  const lowCoverageKey =
    json.params.coverageBasis === 'REFERENCE_SURFACE'
      ? 'stationVolumes.table.lowCoverageRef'
      : 'stationVolumes.table.lowCoverage';
  const store = useStore();
  const selection = useAtomValue(stationVolumeSelectionAtom);
  const setHover = useSetAtom(stationVolumeHoverBinAtom);
  const [walk, setWalk] = useAtom(alignmentWalkAtom);
  const [sorts, setSorts] = useState<StationSort[]>(DEFAULT_SORT);
  const [query, setQuery] = useState('');
  const [onlySelection, setOnlySelection] = useState(false);
  const [showAea, setShowAea] = useState(false);
  const [showCells, setShowCells] = useState(false);
  const [showCrossfall, setShowCrossfall] = useState(false);
  const [scrollTop, setScrollTop] = useState(0);
  const [viewportPx, setViewportPx] = useState(DEFAULT_VIEWPORT_PX);
  /** Height of the in-flow sticky `<thead>`: `scrollTop` 0 is the top of the
   *  TABLE, so every row sits this far further down the scrolled content. */
  const [headPx, setHeadPx] = useState(0);
  /** Height of the sticky `<tfoot>`, which covers the bottom of the viewport. */
  const [footPx, setFootPx] = useState(0);
  /** The row that owns the table's single tab stop, once the user has moved
   *  it with the arrow keys. Null means "wherever the walk cursor is". */
  const [focusedBin, setFocusedBin] = useState<number | null>(null);
  const bodyRef = useRef<HTMLTableSectionElement | null>(null);
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const hoverRef = useRef<number | null>(null);
  const pendingFocusRef = useRef<number | null>(null);
  const ids = useId();

  const sel = selection?.layerId === layerId ? selection : null;
  const selLo = sel ? Math.min(sel.fromBin, sel.toBin) : null;
  const selHi = sel ? Math.max(sel.fromBin, sel.toBin) : null;

  const parsedQuery = useMemo(() => parseStationQuery(query), [query]);
  const queryUnparseable = query.trim().length > 0 && parsedQuery == null;

  const rows = useMemo(() => {
    let base = json.bins;
    if (onlySelection && selLo != null && selHi != null) {
      base = base.filter((b) => b.i >= selLo && b.i <= selHi);
    }
    if (parsedQuery != null) base = base.filter((b) => stationMatchesBin(b, parsedQuery));
    return sortBins(base, sorts, json.stations);
  }, [json.bins, json.stations, onlySelection, selLo, selHi, parsedQuery, sorts]);

  /** Sums exactly the rows on screen, and the footer says so — mixing a
   *  filtered subtotal with a corridor total is how a figure gets mis-quoted. */
  const totals = useMemo(() => totalsForBins(rows), [rows]);
  const [cutTotal, fillTotal, netTotal] = formatM3Row([totals.cut, totals.fill, totals.net]);

  /**
   * One precision for the block of bin volumes {cut, fill, net}, chosen once
   * from the largest of them — `formatM3`'s per-figure rule would print
   * `92.100` above `128.4` in the same column, which reads as a claim about
   * accuracy that the figure below it contradicts, and breaks the decimal
   * alignment `tabular-nums` exists to give.
   *
   * The cumulative column gets its own precision: it is a running total an
   * order of magnitude larger than the bins it sums, so pooling it drags
   * every bin figure to 1 dp and throws away the resolution the reader came
   * for. Two independent quantities, two precisions.
   *
   * Chosen from every bin, not from the filtered rows, so searching or
   * brushing never silently changes the decimals under the reader — and now
   * from the SHARED `m3Formatter`, so the chart tooltip, the 3D tooltip and
   * this column cannot print the same bin at two precisions (ruling A15).
   */
  const fmt = useMemo(() => m3Formatter(json.bins), [json.bins]);
  const volumeCells = useMemo(() => {
    const byBin = new Map<number, { cut: string; fill: string; net: string; cumulative: string }>();
    for (const b of json.bins) {
      byBin.set(b.i, {
        cut: fmt.volume(b.cutVolumeM3),
        fill: fmt.volume(b.fillVolumeM3),
        net: fmt.volume(b.netVolumeM3),
        cumulative: fmt.cumulative(b.cumulativeNetM3),
      });
    }
    return byBin;
  }, [json.bins, fmt]);

  /** The bin the walk cursor is standing in, if it is on this alignment. */
  const walkBinIndex = useMemo(() => {
    if (!walk || walk.key !== json.alignment.key) return null;
    const hit = json.bins.find(
      (b, idx) =>
        walk.station >= b.staStartInternalM &&
        (walk.station < b.staEndInternalM || (idx === json.bins.length - 1 && walk.station <= b.staEndInternalM)),
    );
    return hit ? hit.i : null;
  }, [walk, json.bins, json.alignment.key]);

  // Every column carries a `help` sentence. Two figures on this table are
  // routinely misread and both explanations are load-bearing: cut/fill are
  // VOLUMES over an interval while End area is an AREA at a chainage (m³ vs
  // m²), and AEA is a second m³ for the same interval arrived at a different
  // way. A reader who cannot tell those apart cannot check any of them.
  // One sentence, two places: the column header and the checkbox that reveals
  // the column. Deriving both from here is what stops them drifting apart.
  const aeaHelp = json.stations
    ? t('stationVolumes.table.columnAeaAtStationHelp')
    : t('stationVolumes.table.columnAeaHelp');

  // Run-level, not a user toggle like AEA/Cells: the column exists only when
  // the run itself carried an expectation, which `meanThicknessM` being
  // PRESENT (even as `null`, for a bin that measured nothing) on at least one
  // bin tells us — absent entirely is how a NONE run looks (Task 6).
  const hasThickness = useMemo(() => json.bins.some((b) => b.meanThicknessM !== undefined), [json.bins]);

  // Run-level like the thickness column, but behind a toggle like AEA/Cells:
  // the table is already seven columns wide by default and this adds two.
  // Gated on `params.crossfall` — the RUN carried a check — never on any
  // station line having resolved one, so a corridor the fit could not resolve
  // anywhere still says so instead of silently losing the columns.
  const hasCrossfall = json.params.crossfall != null;
  const crossfallHelp = hasCrossfall
    ? t('stationVolumes.table.columnCrossfallHelp', { tol: json.params.crossfall!.tolerancePct })
    : '';

  const columns: ColumnSpec[] = [
    {
      key: 'station',
      label: t('stationVolumes.table.station'),
      help: t('stationVolumes.table.columnStationHelp'),
      align: 'left',
    },
    {
      key: 'cut',
      label: t('stationVolumes.panel.cut'),
      unit: t('stationVolumes.panel.units'),
      help: t('stationVolumes.table.columnCutHelp'),
      align: 'right',
    },
    {
      key: 'fill',
      label: t('stationVolumes.panel.fill'),
      unit: t('stationVolumes.panel.units'),
      help: t('stationVolumes.table.columnFillHelp'),
      align: 'right',
    },
    {
      key: 'net',
      label: t('stationVolumes.panel.net'),
      unit: t('stationVolumes.panel.units'),
      help: t('stationVolumes.table.columnNetHelp'),
      align: 'right',
    },
    {
      key: 'cumulative',
      label: t('stationVolumes.chart.cumulative'),
      unit: t('stationVolumes.panel.units'),
      help: t('stationVolumes.table.columnCumulativeHelp'),
      align: 'right',
    },
    {
      key: 'coverage',
      label: t('stationVolumes.panel.coverage'),
      unit: t('stationVolumes.table.unitsPct'),
      help: t(
        json.params.coverageBasis === 'REFERENCE_SURFACE'
          ? kind === 'deviation'
            ? 'stationVolumes.table.columnCoverageHelpRefDeviation'
            : 'stationVolumes.table.columnCoverageHelpRef'
          : 'stationVolumes.table.columnCoverageHelp',
        { min: json.params.minCoveragePct },
      ),
      align: 'right',
    },
    {
      key: 'endArea',
      // With true station-line sections the column is the area AT a chainage,
      // not a mean over the interval, and the header has to say which.
      label: json.stations
        ? t('stationVolumes.table.endAreaAtStation')
        : t('stationVolumes.chart.endArea'),
      unit: t('stationVolumes.table.unitsArea'),
      help: json.stations
        ? t('stationVolumes.table.endAreaAtStationHelp')
        : t('stationVolumes.table.endAreaHelp'),
      align: 'right',
    },
    ...(hasThickness
      ? [
          {
            key: 'thickness' as const,
            label: t('stationVolumes.table.columnThickness'),
            help: t('stationVolumes.table.columnThicknessHelp'),
            align: 'right' as const,
          },
        ]
      : []),
    ...(showAea
      ? [
          {
            key: 'aea' as const,
            label: t('stationVolumes.table.columnAea'),
            unit: t('stationVolumes.panel.units'),
            // With station lines the AEA is the mean of two REAL end areas;
            // without them it is the mean of two interval means. Different
            // derivations, so different sentences.
            help: aeaHelp,
            align: 'right' as const,
          },
        ]
      : []),
    ...(showCells
      ? [
          {
            key: 'cells' as const,
            label: t('stationVolumes.table.columnCells'),
            help: cellsHelp,
            align: 'right' as const,
          },
        ]
      : []),
    ...(hasCrossfall && showCrossfall
      ? (['left', 'right'] as const).map((side) => ({
          key: (side === 'left' ? 'crossfallL' : 'crossfallR') as const,
          label: t(
            side === 'left' ? 'stationVolumes.table.columnCrossfallL' : 'stationVolumes.table.columnCrossfallR',
          ),
          unit: t('stationVolumes.table.unitsPct'),
          help: crossfallHelp,
          align: 'right' as const,
        }))
      : []),
  ];

  const colCount = columns.length;

  // ------------------------------------------------------------- windowing
  // Row `i` starts `headPx + i · 28` into the scrolled content, because the
  // `<thead>` is in flow above it.
  const visibleCount = Math.max(1, Math.ceil(viewportPx / ROW_HEIGHT_PX));
  const firstRow = Math.max(
    0,
    Math.min(rows.length, Math.floor(Math.max(0, scrollTop - headPx) / ROW_HEIGHT_PX) - OVERSCAN_ROWS),
  );
  const lastRow = Math.min(rows.length, firstRow + visibleCount + 2 * OVERSCAN_ROWS);
  const windowRows = rows.slice(firstRow, lastRow);
  const padTopPx = firstRow * ROW_HEIGHT_PX;
  const padBottomPx = (rows.length - lastRow) * ROW_HEIGHT_PX;

  // One measurement pass for all three: the viewport and the two sticky
  // bands. Re-run when a column toggle can rewrap the header.
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const measure = () => {
      if (el.clientHeight > 0) setViewportPx(el.clientHeight);
      setHeadPx(el.querySelector<HTMLElement>('thead')?.offsetHeight ?? 0);
      setFootPx(el.querySelector<HTMLElement>('tfoot')?.offsetHeight ?? 0);
    };
    measure();
    if (typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [colCount]);

  /** Put a row inside the visible band, the way `scrollIntoView({ block:
   *  'nearest' })` would — except the row may not be rendered yet, so the
   *  answer comes from `revealScrollTop`'s arithmetic rather than the DOM. */
  const revealRow = useCallback(
    (position: number) => {
      const el = scrollRef.current;
      if (!el || position < 0) return;
      const next = revealScrollTop({
        current: el.scrollTop,
        viewport: el.clientHeight || viewportPx,
        rowTop: headPx + position * ROW_HEIGHT_PX,
        rowH: ROW_HEIGHT_PX,
        headH: headPx,
        footH: footPx,
      });
      if (next == null) return;
      el.scrollTop = next;
      setScrollTop(next);
    },
    [headPx, footPx, viewportPx],
  );

  // Keep the walked row visible when the station is driven from the chart,
  // the 3D bands or the walk toolbar rather than from this table.
  useEffect(() => {
    if (walkBinIndex == null) return;
    revealRow(rows.findIndex((b) => b.i === walkBinIndex));
  }, [walkBinIndex, rows, revealRow]);

  // ----------------------------------------------------------------- hover
  // The hover link between the table, the chart and the 3D bands is a DOM
  // attribute, not React state: 20 000 rows must not re-render because a
  // pointer moved. `data-hover` is styled in TABLE_CSS.
  const applyHover = useCallback(() => {
    const body = bodyRef.current;
    if (!body) return;
    body.querySelector('[data-hover="true"]')?.removeAttribute('data-hover');
    const bin = hoverRef.current;
    if (bin == null) return;
    body.querySelector(`[data-bin="${bin}"]`)?.setAttribute('data-hover', 'true');
  }, []);

  useEffect(() => {
    hoverRef.current = store.get(stationVolumeHoverBinAtom);
    applyHover();
    return store.sub(stationVolumeHoverBinAtom, () => {
      hoverRef.current = store.get(stationVolumeHoverBinAtom);
      applyHover();
    });
  }, [store, applyHover]);

  // The window moves rows in and out of the DOM; re-stamp the attribute on
  // whatever is rendered now.
  useEffect(applyHover, [applyHover, firstRow, rows]);

  // A row focused by an arrow key may only exist after the window has moved.
  useEffect(() => {
    const want = pendingFocusRef.current;
    if (want == null) return;
    const el = bodyRef.current?.querySelector(`[data-bin="${want}"]`);
    if (el instanceof HTMLElement) {
      el.focus();
      pendingFocusRef.current = null;
    }
  });

  // ----------------------------------------------------------- interaction
  const walkTo = (bin: StationBin) => {
    // `follow` and `step` are the user's settings on the walk toolbar; a row
    // click moves the cursor, it does not take over the camera.
    setWalk((w) => (w && w.key === json.alignment.key ? { ...w, station: binCentreStation(bin) } : w));
    setFocusedBin(bin.i);
  };

  const moveFocus = (fromBin: number, step: 1 | -1) => {
    const position = rows.findIndex((b) => b.i === fromBin);
    const next = rows[position + step];
    if (!next) return;
    setFocusedBin(next.i);
    pendingFocusRef.current = next.i;
    revealRow(position + step);
  };

  const onRowKeyDown = (bin: StationBin) => (e: KeyboardEvent<HTMLTableRowElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      walkTo(bin);
      return;
    }
    const step = e.key === 'ArrowDown' ? 1 : e.key === 'ArrowUp' ? -1 : 0;
    if (!step) return;
    e.preventDefault();
    moveFocus(bin.i, step);
  };

  const sortIndex = (key: StationSortKey) => sorts.findIndex((s) => s.key === key);

  const onHeaderClick = (key: StationSortKey) => (e: MouseEvent<HTMLButtonElement>) =>
    setSorts((current) => nextSort(current, key, e.shiftKey));

  // One tab stop into the rows, not one per row: 20 000 rows in the tab order
  // is a keyboard trap by another name. The stop follows the last row the
  // reader arrowed to, then the walk cursor, then the top of the table — so
  // tabbing out and back in returns you where you were.
  const inRows = (bin: number | null) => bin != null && rows.some((b) => b.i === bin);
  const rovingBin = inRows(focusedBin) ? focusedBin : inRows(walkBinIndex) ? walkBinIndex : rows[0]?.i;

  return (
    <div className='flex h-full flex-col gap-2'>
      <style>{TABLE_CSS}</style>

      {/* ---- Controls: search, filters, columns ---- */}
      <div className='flex flex-wrap items-center gap-x-3 gap-y-2'>
        <div className='flex items-center gap-1.5'>
          <label htmlFor={`${ids}-search`} className='text-slate-600 dark:text-stone-300'>
            {t('stationVolumes.table.searchSta')}
          </label>
          <Input
            id={`${ids}-search`}
            data-testid='sv-table-search'
            type='search'
            inputMode='text'
            value={query}
            aria-invalid={queryUnparseable || undefined}
            aria-describedby={queryUnparseable ? `${ids}-search-error` : undefined}
            placeholder={t('stationVolumes.table.searchStaPlaceholder')}
            onChange={(e) => setQuery(e.target.value)}
            // One clear affordance, not two: WebKit's own search ✕ would sit
            // beside the Clear button that Firefox needs.
            className='sv-sta h-7 w-36 text-xs [&::-webkit-search-cancel-button]:appearance-none'
          />
          {query.length > 0 && (
            <Button
              data-testid='sv-table-search-clear'
              type='button'
              size='sm'
              variant='ghost'
              className='h-7 px-2'
              onClick={() => setQuery('')}>
              {t('stationVolumes.table.searchStaClear')}
            </Button>
          )}
          {queryUnparseable && (
            <span id={`${ids}-search-error`} className='text-[var(--sv-warn)]'>
              {t('stationVolumes.errors.stationUnparseable')}
            </span>
          )}
        </div>

        {sel && (
          <div className='flex items-center gap-1.5'>
            <Checkbox
              id={`${ids}-only-selection`}
              data-testid='sv-table-only-selection'
              checked={onlySelection}
              onCheckedChange={(c) => setOnlySelection(!!c)}
            />
            <label htmlFor={`${ids}-only-selection`}>{t('stationVolumes.table.onlySelection')}</label>
          </div>
        )}

        <div className='flex items-center gap-1.5'>
          <span className='text-slate-500 dark:text-stone-400'>{t('stationVolumes.table.columns')}</span>
          <Checkbox
            id={`${ids}-col-aea`}
            data-testid='sv-table-col-aea'
            checked={showAea}
            onCheckedChange={(c) => setShowAea(!!c)}
          />
          <label htmlFor={`${ids}-col-aea`} title={aeaHelp}>
            {t('stationVolumes.table.columnAea')}
          </label>
          <Checkbox
            id={`${ids}-col-cells`}
            data-testid='sv-table-col-cells'
            checked={showCells}
            onCheckedChange={(c) => setShowCells(!!c)}
          />
          <label htmlFor={`${ids}-col-cells`} title={cellsHelp}>
            {t('stationVolumes.table.columnCells')}
          </label>
          {hasCrossfall && (
            <>
              <Checkbox
                id={`${ids}-col-crossfall`}
                data-testid='sv-table-col-crossfall'
                checked={showCrossfall}
                onCheckedChange={(c) => setShowCrossfall(!!c)}
              />
              <label htmlFor={`${ids}-col-crossfall`} title={crossfallHelp}>
                {t('stationVolumes.table.columnCrossfall')}
              </label>
            </>
          )}
        </div>

        {!isDefaultSort(sorts) && (
          <Button
            data-testid='sv-table-reset-order'
            type='button'
            size='sm'
            variant='ghost'
            className='h-7 px-2'
            onClick={() => setSorts(DEFAULT_SORT)}>
            {t('stationVolumes.table.resetOrder')}
          </Button>
        )}

        <span className='ml-auto text-slate-500 dark:text-stone-400'>{t('stationVolumes.table.hint')}</span>
      </div>

      {/* The dock body never scrolls sideways: the overflow lives here, and
          the header and footer stay put while the rows move under them. */}
      <div
        ref={scrollRef}
        data-testid='sv-table-scroll'
        onScroll={(e) => setScrollTop(e.currentTarget.scrollTop)}
        className='min-h-0 flex-1 overflow-auto'>
        <table className='sv-num w-full border-collapse text-right'>
          <caption className='sr-only'>
            {t('stationVolumes.table.caption', {
              bins: json.bins.length,
              from: staOnly(json.bins[0]?.staStartDisplayM ?? 0),
              to: staOnly(json.bins[json.bins.length - 1]?.staEndDisplayM ?? 0),
            })}
          </caption>
          <thead>
            <tr>
              {columns.map((col) => {
                const at = sortIndex(col.key);
                const dir = at >= 0 ? sorts[at].dir : null;
                return (
                  <th
                    key={col.key}
                    scope='col'
                    // ARIA asks for one sorted header at a time, so the
                    // secondary sort is announced in text on the button
                    // instead of as a second aria-sort. Every sortable
                    // header carries the attribute, so a screen reader is
                    // told which columns *can* be sorted.
                    aria-sort={at === 0 ? (dir === 1 ? 'ascending' : 'descending') : 'none'}
                    className={`sticky top-0 z-10 bg-[var(--sv-surface)] px-2 py-1 font-semibold whitespace-nowrap ${
                      col.align === 'left' ? 'text-left' : 'text-right'
                    }`}>
                    <button
                      type='button'
                      data-testid={`sv-th-${col.key}`}
                      title={
                        col.help
                          ? `${col.help} — ${t('stationVolumes.table.sortHelp')}`
                          : t('stationVolumes.table.sortHelp')
                      }
                      onClick={onHeaderClick(col.key)}
                      className='inline-flex min-h-6 items-center gap-1 rounded px-0.5 focus-visible:ring-2 focus-visible:ring-sky-500 focus-visible:outline-none'>
                      <span>{col.label}</span>
                      {col.unit && <span className='sv-unit font-normal'>{col.unit}</span>}
                      <span aria-hidden className={at >= 0 ? '' : 'opacity-30'}>
                        {at < 0 ? '↕' : dir === 1 ? '▲' : '▼'}
                      </span>
                      {at === 1 && (
                        <span className='sr-only'>
                          {`${t('stationVolumes.table.sortSecondary')} — ${
                            dir === 1
                              ? t('stationVolumes.table.sortedAscending')
                              : t('stationVolumes.table.sortedDescending')
                          }`}
                        </span>
                      )}
                    </button>
                  </th>
                );
              })}
            </tr>
          </thead>

          <tbody ref={bodyRef}>
            {/* A station that matches nothing is the only empty state this
                body has — an unparseable one filters nothing, so it can never
                produce "No bins match ." */}
            {rows.length === 0 && parsedQuery != null && (
              <tr>
                <td
                  colSpan={colCount}
                  data-testid='sv-table-empty'
                  className='px-2 py-4 text-left text-slate-500 dark:text-stone-400'>
                  {t('stationVolumes.table.noMatch', { station: staOnly(parsedQuery) })}
                </td>
              </tr>
            )}
            {/* Spacers stand in for the rows above and below the window, so
                the scrollbar describes the whole table and not just the slice
                that happens to be rendered. */}
            {padTopPx > 0 && (
              <tr aria-hidden data-testid='sv-pad-top' style={{ height: padTopPx }}>
                <td colSpan={colCount} />
              </tr>
            )}
            {windowRows.map((b) => {
              const inSelection = selLo == null || selHi == null || (b.i >= selLo && b.i <= selHi);
              const walked = walkBinIndex === b.i;
              const cells = volumeCells.get(b.i);
              return (
                <tr
                  key={b.i}
                  data-bin={b.i}
                  data-testid={`sv-row-${b.i}`}
                  // Focus is an OUTLINE, not a ring: a ring is a box-shadow,
                  // and a box-shadow on a row of a `border-collapse` table is
                  // not painted — the focus would be invisible exactly where
                  // a keyboard user needs it.
                  className='sv-row cursor-pointer border-b border-[var(--sv-grid)] focus-visible:-outline-offset-2 focus-visible:outline-2 focus-visible:outline-sky-500'
                  tabIndex={rovingBin === b.i ? 0 : -1}
                  aria-current={walked ? 'location' : undefined}
                  style={{
                    height: ROW_HEIGHT_PX,
                    // 55 % outside the brush: still readable, plainly not the
                    // range the header's totals are talking about.
                    opacity: inSelection ? undefined : 0.55,
                  }}
                  onMouseEnter={() => setHover(b.i)}
                  onMouseLeave={() => setHover(null)}
                  onFocus={() => setFocusedBin(b.i)}
                  onClick={() => walkTo(b)}
                  onKeyDown={onRowKeyDown(b)}>
                  <td className='sv-sta px-2 py-1.5 text-left whitespace-nowrap'>{formatStationRange(b)}</td>
                  <td className='px-2 py-1.5' style={{ color: 'var(--sv-cut)' }}>
                    {cells?.cut}
                  </td>
                  <td className='px-2 py-1.5' style={{ color: 'var(--sv-fill)' }}>
                    {cells?.fill}
                  </td>
                  <td className='px-2 py-1.5'>{cells?.net}</td>
                  <td className='px-2 py-1.5'>{cells?.cumulative}</td>
                  <td
                    className='px-2 py-1.5 whitespace-nowrap'
                    style={b.lowCoverage ? { color: 'var(--sv-warn)' } : undefined}>
                    {formatPct(b.coveragePct)}
                    {b.lowCoverage && (
                      <span title={t(lowCoverageKey, { min: json.params.minCoveragePct })}>
                        <span aria-hidden> ⚠</span>
                        <span className='sr-only'>
                          {` ${t(lowCoverageKey, { min: json.params.minCoveragePct })}`}
                        </span>
                      </span>
                    )}
                  </td>
                  <td className='px-2 py-1.5 whitespace-nowrap'>
                    {(() => {
                      const a = endAreaFor(b, json.stations);
                      return `${formatM2(a.cut)} / ${formatM2(a.fill)}`;
                    })()}
                  </td>
                  {/* One cell, not two: this table renders exactly one `<td>`
                      per `columns` entry (no colSpan machinery), so a
                      separately-headed "Verdict" column would need its own
                      `<th>` — but the verdict has no sort of its own (only
                      `thickness` does), and `sv-th-thickness` is the only
                      header the brief's tests ask for. The glyph rides in the
                      same cell as the figure it judges instead; it is still
                      the non-interactive badge Task 14 calls for, never the
                      openable `VerdictChip` (that belongs once, corridor-wide,
                      in the dock header — Step 3b). */}
                  {hasThickness && (
                    <td
                      data-testid='sv-bin-verdict'
                      data-verdict={b.verdict}
                      title={b.verdict ? t(`expectation.verdictHelp.${b.verdict}`) : undefined}
                      className={`px-2 py-1.5 whitespace-nowrap ${b.verdict ? SEVERITY[b.verdict].cls : ''}`}>
                      {b.meanThicknessM == null ? (
                        <span title={t('stationVolumes.table.thicknessNa')}>
                          <span aria-hidden>—</span>
                          <span className='sr-only'>{t('stationVolumes.table.thicknessNa')}</span>
                        </span>
                      ) : (
                        mToMm(b.meanThicknessM).toFixed(1)
                      )}
                      {b.verdict && (
                        <>
                          {' '}
                          <span aria-hidden='true'>{SEVERITY[b.verdict].glyph}</span>
                          <span className='sr-only'>{t(`expectation.verdict.${b.verdict}`)}</span>
                        </>
                      )}
                    </td>
                  )}
                  {showAea && (
                    <td className='px-2 py-1.5 whitespace-nowrap'>
                      {b.cutVolumeAeaM3 == null || b.fillVolumeAeaM3 == null ? (
                        <span title={t('stationVolumes.table.aeaNa')}>
                          <span aria-hidden>—</span>
                          <span className='sr-only'>{t('stationVolumes.table.aeaNa')}</span>
                        </span>
                      ) : (
                        `${fmt.volume(b.cutVolumeAeaM3)} / ${fmt.volume(b.fillVolumeAeaM3)}`
                      )}
                    </td>
                  )}
                  {showCells && <td className='px-2 py-1.5'>{b.cellCount}</td>}
                  {hasCrossfall &&
                    showCrossfall &&
                    (['left', 'right'] as const).map((side) => {
                      const c = crossfallFor(b, json.stations, side);
                      // Three absences print the same em dash, but they do
                      // not have the same cause: a side the RUN never
                      // configured is a whole column of dashes, and "the fit
                      // could not resolve this band" would blame a fit nobody
                      // asked for.
                      const hasBand = json.params.crossfall?.[side] != null;
                      const naText = t(
                        hasBand ? 'stationVolumes.table.crossfallNa' : 'stationVolumes.table.crossfallNoBand',
                      );
                      return (
                        <td
                          key={side}
                          data-testid={`sv-crossfall-${side}-cell`}
                          data-verdict={c?.verdict}
                          title={c?.reason ? t(`stationVolumes.table.crossfallReason.${c.reason}`) : undefined}
                          className={`px-2 py-1.5 whitespace-nowrap ${c?.verdict ? SEVERITY[c.verdict].cls : ''}`}>
                          {c?.deltaPct == null || c.asbuiltPct == null ? (
                            <span title={naText}>
                              <span aria-hidden>—</span>
                              <span className='sr-only'>{naText}</span>
                            </span>
                          ) : (
                            // "as-built (Δ)", the shape the spec asks for.
                            // `formatSigned` keeps the U+2212 minus the rest
                            // of this dock prints, so a negative crossfall
                            // does not read as a hyphen.
                            `${formatSigned(c.asbuiltPct, 2)} (${formatSigned(c.deltaPct, 2)})`
                          )}
                          {c?.verdict && (
                            <>
                              {' '}
                              <span aria-hidden='true'>{SEVERITY[c.verdict].glyph}</span>
                              <span className='sr-only'>{t(`expectation.verdict.${c.verdict}`)}</span>
                            </>
                          )}
                        </td>
                      );
                    })}
                </tr>
              );
            })}
            {padBottomPx > 0 && (
              <tr aria-hidden data-testid='sv-pad-bottom' style={{ height: padBottomPx }}>
                <td colSpan={colCount} />
              </tr>
            )}
          </tbody>

          <tfoot>
            <tr className='border-t border-[var(--sv-grid)] font-semibold'>
              <th
                scope='row'
                data-testid='sv-table-total-label'
                className='sticky bottom-0 bg-[var(--sv-surface)] px-2 py-1.5 text-left font-semibold whitespace-nowrap'>
                {t('stationVolumes.table.totalsShown', { bins: rows.length })}
              </th>
              <td className='sticky bottom-0 bg-[var(--sv-surface)] px-2 py-1.5' data-testid='sv-table-total-cut'>
                {cutTotal}
              </td>
              <td className='sticky bottom-0 bg-[var(--sv-surface)] px-2 py-1.5' data-testid='sv-table-total-fill'>
                {fillTotal}
              </td>
              <td className='sticky bottom-0 bg-[var(--sv-surface)] px-2 py-1.5' data-testid='sv-table-total-net'>
                {netTotal}
              </td>
              {/* Cumulative, coverage and the areas have no meaningful total —
                  an empty cell says that better than a number nobody can use. */}
              <td colSpan={colCount - 4} className='sticky bottom-0 bg-[var(--sv-surface)] px-2 py-1.5' />
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
};
